import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  CreditCard, Download, AlertCircle, Check, Settings,
  Shield, Bell, FileText, TrendingUp, Smartphone, Lock, CheckCircle2, XCircle, Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── P0.2 MFA Panel ────────────────────────────────────────────────────────
function MFAPanel({ user }) {
  const [step, setStep] = useState("idle"); // idle | setup | activate | disable
  const [qrCode, setQrCode] = useState(null);
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfa_enabled || false);

  const apiHeaders = () => {
    const token = localStorage.getItem('lawliet_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, { headers: apiHeaders(), withCredentials: true });
      setQrCode(res.data.qr_code_base64);
      setManualKey(res.data.manual_entry_key);
      setStep("setup");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la configuration MFA");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (code.length !== 6) { toast.error("Le code doit comporter 6 chiffres"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/activate?code=${code}`, {}, { headers: apiHeaders(), withCredentials: true });
      toast.success("✅ MFA activé avec succès !");
      setMfaEnabled(true);
      setStep("idle");
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (code.length !== 6) { toast.error("Le code doit comporter 6 chiffres"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable?code=${code}`, {}, { headers: apiHeaders(), withCredentials: true });
      toast.success("MFA désactivé");
      setMfaEnabled(false);
      setStep("idle");
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${mfaEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
              <Smartphone className={`h-5 w-5 ${mfaEnabled ? 'text-green-700' : 'text-slate-500'}`} />
            </div>
            <div>
              <CardTitle className="text-base">Authentification à Deux Facteurs (2FA / MFA)</CardTitle>
              <CardDescription>Protégez votre compte avec un code TOTP (Google Authenticator, Authy…)</CardDescription>
            </div>
          </div>
          {mfaEnabled
            ? <Badge className="bg-green-100 text-green-800 gap-1"><CheckCircle2 className="h-3 w-3"/>Activé</Badge>
            : <Badge variant="outline" className="gap-1"><XCircle className="h-3 w-3 text-slate-400"/>Désactivé</Badge>
          }
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === "idle" && !mfaEnabled && (
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2 text-sm text-amber-800">
              <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Le MFA ajoute une couche de sécurité obligatoire recommandée pour les cabinets d'avocats. En cas de vol de mot de passe, votre compte reste protégé.</span>
            </div>
            <Button onClick={handleSetup} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? "Chargement..." : "Configurer le MFA"}
            </Button>
          </div>
        )}

        {step === "setup" && qrCode && (
          <div className="space-y-4">
            <p className="text-sm text-slate-700 font-medium">1. Scannez ce QR code avec votre application d'authentification :</p>
            <div className="flex justify-center">
              <img src={`data:image/png;base64,${qrCode}`} alt="QR Code MFA" className="border rounded-lg p-2 bg-white w-48 h-48" />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Ou entrez manuellement cette clé :</p>
              <code className="text-xs font-mono bg-white p-1.5 rounded border block break-all">{manualKey}</code>
            </div>
            <p className="text-sm text-slate-700 font-medium">2. Entrez le code à 6 chiffres pour confirmer :</p>
            <div className="flex gap-2">
              <Input
                placeholder="000000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                className="w-32 text-center text-lg tracking-widest font-mono"
              />
              <Button onClick={handleActivate} disabled={loading || code.length !== 6} className="bg-green-700 hover:bg-green-800">
                {loading ? "Vérification..." : "Valider"}
              </Button>
              <Button variant="outline" onClick={() => { setStep("idle"); setCode(""); }}>Annuler</Button>
            </div>
          </div>
        )}

        {step === "idle" && mfaEnabled && (
          <div className="space-y-3">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex gap-2 text-sm text-green-800">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Votre compte est protégé par l'authentification à deux facteurs. Un code TOTP sera demandé à chaque connexion.</span>
            </div>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setStep("disable")}>
              Désactiver le MFA
            </Button>
          </div>
        )}

        {step === "disable" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-700">Entrez votre code actuel pour confirmer la désactivation :</p>
            <div className="flex gap-2">
              <Input
                placeholder="000000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                className="w-32 text-center text-lg tracking-widest font-mono"
              />
              <Button onClick={handleDisable} disabled={loading || code.length !== 6} variant="destructive">
                {loading ? "..." : "Désactiver"}
              </Button>
              <Button variant="outline" onClick={() => { setStep("idle"); setCode(""); }}>Annuler</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsBilling({ user, cabinet, onLogout }) {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sbTheme, setSbTheme] = useState(
    () => localStorage.getItem('lawliet_sidebar_theme') || 'light'
  );
  const [hourlyRate, setHourlyRate] = useState(user?.hourly_rate_euros ?? 0);
  const [rateSaving, setRateSaving] = useState(false);

  const toggleSbTheme = () => {
    const next = sbTheme === 'light' ? 'dark' : 'light';
    localStorage.setItem('lawliet_sidebar_theme', next);
    setSbTheme(next);
    window.location.reload();
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load subscription
      const subRes = await axios.get(`${API}/billing/subscription`, { headers });
      setSubscription(subRes.data);

      // Load usage
      const usageRes = await axios.get(`${API}/billing/usage`, { headers });
      setUsage(usageRes.data);

      // Load alerts
      const alertsRes = await axios.get(`${API}/billing/alerts`, { headers });
      setAlerts(alertsRes.data.alerts || []);

      // Load invoices
      const invRes = await axios.get(`${API}/billing/invoices`, { headers });
      setInvoices(invRes.data.invoices || []);

    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/billing/invoices/${invoiceId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Facture téléchargée");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.post(`${API}/billing/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Alerte acquittée");
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/gdpr/export-data?format=json`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lawliet_mes_donnees.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé avec succès");
    } catch (error) {
      toast.error("Erreur lors de l'export de vos données");
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.post(
        `${API}/gdpr/deletion-request`,
        { confirm_deletion: true, delete_documents: true, delete_messages: true, delete_contracts: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Demande de suppression enregistrée. Vérifiez votre email.");
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la demande de suppression");
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleSaveHourlyRate = async () => {
    setRateSaving(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.patch(
        `${API}/auth/me/hourly-rate`,
        { hourly_rate_euros: Number(hourlyRate) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Taux horaire mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setRateSaving(false);
    }
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return "text-red-600";
    if (percent >= 80) return "text-amber-600";
    return "text-green-600";
  };

  const getPlanBadgeColor = (status) => {
    if (status === "active") return "bg-green-100 text-green-800";
    if (status === "trial") return "bg-blue-100 text-blue-800";
    return "bg-slate-100 text-slate-800";
  };

  if (loading) {
    return (
      <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
        <div className="text-center py-12">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-blue-900">Paramètres et Facturation</h1>
          <p className="text-slate-600 mt-1">Gérez votre abonnement et vos paramètres</p>
        </div>

        <Tabs defaultValue="subscription" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="subscription">
              <CreditCard className="h-4 w-4 mr-2" />
              Abonnement
            </TabsTrigger>
            <TabsTrigger value="usage">
              <TrendingUp className="h-4 w-4 mr-2" />
              Utilisation
            </TabsTrigger>
            <TabsTrigger value="invoices">
              <FileText className="h-4 w-4 mr-2" />
              Factures
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Shield className="h-4 w-4 mr-2" />
              Sécurité
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          {/* Subscription Tab */}
          <TabsContent value="subscription" className="space-y-4">
            {subscription?.subscription ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Plan Actuel</CardTitle>
                      <CardDescription>Détails de votre abonnement</CardDescription>
                    </div>
                    <Badge className={getPlanBadgeColor(subscription.subscription.status)}>
                      {subscription.subscription.status === "trial" ? "Essai Gratuit" : "Actif"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Plan</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {subscription.plan?.name || "N/A"}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        {subscription.plan?.description}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Prix</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {subscription.plan?.price_monthly_cents 
                          ? `${(subscription.plan.price_monthly_cents / 100).toFixed(2)}€`
                          : "N/A"}
                        <span className="text-base font-normal text-slate-600">/mois</span>
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-3">Fonctionnalités incluses</p>
                    <div className="grid md:grid-cols-2 gap-2">
                      {subscription.plan?.features?.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <Check className="h-4 w-4 text-green-600 mt-0.5" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1">
                      Mettre à niveau
                    </Button>
                    <Button variant="outline" className="text-red-600 hover:text-red-700">
                      Annuler l'abonnement
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-slate-600">Aucun abonnement actif</p>
                  <Button className="mt-4 bg-blue-600">Choisir un plan</Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage" className="space-y-4">
            {usage?.usage ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Utilisation Actuelle</CardTitle>
                    <CardDescription>
                      Période: {new Date(usage.period?.start).toLocaleDateString()} - {new Date(usage.period?.end).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Documents */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">Documents analysés</p>
                        <p className={`text-sm font-bold ${getUsageColor(usage.usage.documents?.percent)}`}>
                          {usage.usage.documents?.current || 0} / {usage.usage.documents?.limit || 0}
                          <span className="ml-2">({usage.usage.documents?.percent?.toFixed(0)}%)</span>
                        </p>
                      </div>
                      <Progress value={usage.usage.documents?.percent || 0} className="h-2" />
                    </div>

                    {/* AI Queries */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">Requêtes IA</p>
                        <p className={`text-sm font-bold ${getUsageColor(usage.usage.ai_queries?.percent)}`}>
                          {usage.usage.ai_queries?.current || 0} / {usage.usage.ai_queries?.limit || 0}
                          <span className="ml-2">({usage.usage.ai_queries?.percent?.toFixed(0)}%)</span>
                        </p>
                      </div>
                      <Progress value={usage.usage.ai_queries?.percent || 0} className="h-2" />
                    </div>

                    {/* Contracts */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">Contrats générés</p>
                        <p className={`text-sm font-bold ${getUsageColor(usage.usage.contracts?.percent)}`}>
                          {usage.usage.contracts?.current || 0} / {usage.usage.contracts?.limit || 0}
                          <span className="ml-2">({usage.usage.contracts?.percent?.toFixed(0)}%)</span>
                        </p>
                      </div>
                      <Progress value={usage.usage.contracts?.percent || 0} className="h-2" />
                    </div>

                    {/* Storage */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">Stockage utilisé</p>
                        <p className={`text-sm font-bold ${getUsageColor(usage.usage.storage?.percent)}`}>
                          {(usage.usage.storage?.current_mb / 1024).toFixed(2)} Go / {usage.usage.storage?.limit_gb} Go
                          <span className="ml-2">({usage.usage.storage?.percent?.toFixed(0)}%)</span>
                        </p>
                      </div>
                      <Progress value={usage.usage.storage?.percent || 0} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                {/* Alerts */}
                {alerts.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-amber-600" />
                        Alertes de Consommation
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {alerts.map(alert => (
                        <div key={alert.id} className="flex items-start justify-between bg-white p-3 rounded border">
                          <div className="flex items-start gap-2">
                            <AlertCircle className={`h-5 w-5 mt-0.5 ${alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
                            <div>
                              <p className="text-sm font-semibold">{alert.message}</p>
                              <p className="text-xs text-slate-600 mt-1">
                                {new Date(alert.created_at).toLocaleString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => acknowledgeAlert(alert.id)}>
                            Acquitter
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-slate-600">
                  Aucune donnée d'utilisation disponible
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            {invoices.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Historique des Factures</CardTitle>
                  <CardDescription>{invoices.length} facture(s)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {invoices.map(invoice => (
                      <div key={invoice.id} className="flex items-center justify-between p-4 border rounded hover:bg-slate-50">
                        <div>
                          <p className="font-semibold">{invoice.invoice_number}</p>
                          <p className="text-sm text-slate-600">
                            {new Date(invoice.issue_date).toLocaleDateString('fr-FR')}
                          </p>
                          <Badge className={invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                            {invoice.status === 'paid' ? 'Payée' : 'En attente'}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-900">
                            {(invoice.total_cents / 100).toFixed(2)}€
                          </p>
                          <Button size="sm" variant="outline" onClick={() => downloadInvoice(invoice.id)} className="mt-2">
                            <Download className="h-4 w-4 mr-2" />
                            Télécharger
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-slate-600">
                  Aucune facture disponible
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Apparence</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Thème de la sidebar</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {sbTheme === 'light' ? 'Clair (Frosted White)' : 'Sombre (Dark Slate)'}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={toggleSbTheme}>
                    {sbTheme === 'light' ? '🌙 Passer en sombre' : '☀️ Passer en clair'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4 text-blue-600" />
                  Mon taux horaire
                </CardTitle>
                <CardDescription>
                  Taux utilisé par défaut pour la facturation client. Peut être surchargé par dossier.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min="0"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    className="w-36"
                    placeholder="0"
                  />
                  <span className="text-sm text-slate-500">€ / heure HT</span>
                  <Button onClick={handleSaveHourlyRate} disabled={rateSaving} size="sm">
                    {rateSaving ? 'Enregistrement…' : 'Enregistrer'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Sécurité et Confidentialité (RGPD)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleExportData}
                    disabled={exportingData}
                    data-testid="export-gdpr-btn"
                  >
                    {exportingData ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {exportingData ? "Export en cours..." : "Exporter mes données (RGPD)"}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Télécharger toutes vos données personnelles conformément au RGPD (Article 15)
                  </p>
                </div>

                <Separator />

                {!showDeleteConfirm ? (
                  <div>
                    <Button
                      variant="outline"
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setShowDeleteConfirm(true)}
                      data-testid="delete-account-btn"
                    >
                      Supprimer mon compte
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">
                      Action irréversible — Toutes vos données seront supprimées (RGPD Article 17)
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                    <p className="text-sm font-semibold text-red-800">Confirmer la suppression du compte ?</p>
                    <p className="text-xs text-red-700">
                      Cette action est irréversible. Tous vos documents, conversations et contrats seront définitivement supprimés.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        data-testid="confirm-delete-account-btn"
                      >
                        {deletingAccount ? "Suppression..." : "Confirmer la suppression"}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab — P0.2 MFA */}
          <TabsContent value="security" className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-800 mb-1">Sécurité du Compte</h2>
              <p className="text-sm text-slate-500">Gérez l'authentification forte et la protection de votre accès</p>
            </div>
            <MFAPanel user={user} />
          </TabsContent>

        </Tabs>
      </div>
    </Layout>
  );
}
